import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AppointmentDetailFragment, CustomerDetailFragment } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useEditCustomerForAppointment } from "../hooks/use-edit-customer-for-appointment";

interface EditCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: AppointmentDetailFragment;
  customer: CustomerDetailFragment;
}

export function EditCustomerDialog({
  open,
  onOpenChange,
  appointment,
  customer,
}: EditCustomerDialogProps) {
  const { currentSalon } = useSalonStore();
  const { editCustomer, isUpdating } = useEditCustomerForAppointment();
  const { t: tForms } = useTranslation("forms");
  const { t: tErrors } = useTranslation("errors");

  const activeCustomerPhones = customer.phoneNumbers?.filter((p) => p.isActive) ?? [];
  const fallbackCustomerPhone =
    customer.primaryPhone?.phone ||
    activeCustomerPhones.find((p) => p.isPrimary)?.phone ||
    activeCustomerPhones[0]?.phone ||
    "";

  // Edit customer fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const wasOpenRef = useRef(false);
  const lastContextRef = useRef<{ appointmentId: string; customerId: string } | null>(null);

  // Initialize form only when the dialog transitions closed -> open.
  // Avoid re-initializing while open, which causes value "flip-flopping" during refetches.
  useEffect(() => {
    const context = { appointmentId: appointment.id, customerId: customer.id };

    if (!open) {
      wasOpenRef.current = false;
      lastContextRef.current = context;
      return;
    }

    const openingNow = !wasOpenRef.current;
    const contextChangedWhileClosed =
      lastContextRef.current?.appointmentId !== context.appointmentId ||
      lastContextRef.current?.customerId !== context.customerId;

    if (openingNow || contextChangedWhileClosed) {
      setFirstName(customer.firstName);
      setLastName(customer.lastName);
      setPhone(appointment.bookingPhone?.phone || fallbackCustomerPhone);
      setEmail(customer.email || "");
    }

    wasOpenRef.current = true;
    lastContextRef.current = context;
  }, [
    open,
    appointment.id,
    appointment.bookingPhone?.phone,
    customer.id,
    customer.firstName,
    customer.lastName,
    customer.email,
    fallbackCustomerPhone,
  ]);

  const handleSave = async () => {
    if (!currentSalon) return;

    // Validate required fields
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(tErrors("forms.requiredField"));
      return;
    }

    const success = await editCustomer({
      salonId: currentSalon.id,
      appointmentId: appointment.id,
      customerId: customer.id,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || null,
      phone: phone.trim(),
      originalPhone: appointment.bookingPhone?.phone || fallbackCustomerPhone,
      // Pass all existing phones to prevent deletion
      existingPhones:
        customer.phoneNumbers
          ?.filter((p) => p.isActive)
          .map((p) => ({
            id: p.id,
            phone: p.phone,
            isPrimary: p.isPrimary,
            label: p.label ?? null,
          })) || [],
    });

    if (success) {
      onOpenChange(false);
    }
  };

  const isFormValid = () => {
    return firstName.trim() && lastName.trim();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{tForms("titles.editCustomer")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{tForms("labels.firstName")}</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={tForms("placeholders.firstName")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{tForms("labels.lastName")}</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={tForms("placeholders.lastName")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{tForms("labels.phone")}</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={tForms("placeholders.phone")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{tForms("labels.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={tForms("placeholders.email")}
            />
          </div>
        </div>

        <DialogFooter className="gap-3 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            {tForms("actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid() || isUpdating}>
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {tForms("actions.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
